import { useMemo, useState } from 'react';

import { Combobox, type ComboOption } from './Combobox';
import { searchStores, storeLabel, type Store } from './data';

interface Props {
    stores: Store[];
    value: string;
    onChange: (storeId: string) => void;
}

/**
 * Search-then-pick, or just pick.
 *
 * The menu opens populated on focus so an associate can scroll to their store
 * without knowing anything to type; typing narrows it. Number matches come
 * first, because most associates know their store number.
 *
 * Every match is shown rather than a capped page - the menu scrolls, and a
 * silently truncated list is how someone concludes their store is missing.
 */
export function StorePicker({ stores, value, onChange }: Props) {
    const [query, setQuery] = useState('');
    const matches = useMemo(
        () =>
            query.trim() === ''
                ? stores
                : searchStores(stores, query, stores.length),
        [stores, query],
    );
    const chosen = stores.find((s) => s.id === value);

    const options: ComboOption[] = matches.map((s) => ({
        id: s.id,
        primary: storeLabel(s),
        secondary: `Store ${s.id}`,
    }));

    if (chosen) {
        return (
            <div className="chosen">
                <span className="chosen-body">
                    <b>{storeLabel(chosen)}</b>
                    <span>Store {chosen.id}</span>
                </span>
                <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                        setQuery('');
                        onChange('');
                    }}
                >
                    Change store
                </button>
            </div>
        );
    }

    return (
        <Combobox
            label="Find your store"
            placeholder="Store number or city"
            query={query}
            onQueryChange={setQuery}
            options={options}
            onSelect={(id) => {
                setQuery('');
                onChange(id);
            }}
            // Opens on click: an associate should be able to scroll to their
            // store without knowing anything to type.
            openOnFocus
            emptyHint="Type your store number, or the city you are in."
            noMatchHint="No store matches that. Try just the number."
        />
    );
}
